import React from 'react';
import transition from '../../transition';
import CaseStudy from '../CaseStudy/CaseStudy';
import '../Project.css';

import hero from '../../assets/images/projects/crimson-flags/crimsonflags-hero.png';
import CrimsonFlagsImage1 from '../../assets/images/projects/crimson-flags/crimsonflags1.png';
import CrimsonFlagsImage2 from '../../assets/images/projects/crimson-flags/crimsonflags2.png';
import CrimsonFlagsImage3 from '../../assets/images/projects/crimson-flags/crimsonflags3.png';
import CrimsonFlagsImage4 from '../../assets/images/projects/crimson-flags/crimsonflags4.png';
import CrimsonFlagsImage5 from '../../assets/images/projects/crimson-flags/crimsonflags5.png';
import CrimsonFlagsImage6 from '../../assets/images/projects/crimson-flags/crimsonflags6.png';
import CrimsonFlagsImage7 from '../../assets/images/projects/crimson-flags/crimsonflags7.png';
import CrimsonFlagsImage8 from '../../assets/images/projects/crimson-flags/crimsonflags8.png';
import CrimsonFlagsImage9 from '../../assets/images/projects/crimson-flags/crimsonflags9.png';

const CrimsonFlags = () => {
  return (
    <CaseStudy
      className="crimsonFlags"
      accent="var(--crimsonFlags)"
      eyebrow="Capstone · Roommate matching"
      title="Crimson Flags"
      summary="An award-winning roommate matching app for Indiana University students, built as my Luddy Informatics capstone."
      links={[
        {
          href: 'https://zion.luddy.indiana.edu/info-capstone-2022/crimson-flags',
          label: 'Luddy project page'
        },
        {
          href: '#frames',
          label: 'See it in action'
        }
      ]}
      hero={{
        src: hero,
        alt: 'Crimson Flags matches desktop and chat mobile composition',
        caption: 'Crimson Flags · matches + chat'
      }}
      date="Aug. 2021 – May 2022"
      role="Project lead & lead web developer"
      stack={['PHP', 'MySQL', 'JavaScript', 'HTML/CSS', 'Figma']}
      brief={{
        label: 'Background',
        they: {
          title: 'The problem space',
          body: 'IU students hunt for roommates every year through Facebook groups, group chats, and luck. That process is messy, hard to verify, and weak at catching lifestyle mismatches before someone signs a lease.'
        },
        me: {
          title: 'My role',
          body: 'Crimson Flags was a four-person Informatics capstone team. I was project lead and the lead web developer: I shaped the product direction and implemented most of the application features across two semesters of planning, then build.'
        }
      }}
      problem={{
        title: 'Finding a roommate shouldn’t feel like a gamble.',
        lead: 'Students needed a safer, clearer way to match on lifestyle, not just who answered a Facebook post first.',
        detail:
          'Our capstone brief was to ship a real information system for an IU student problem. Roommate search kept coming up: people needed verification, honest dealbreakers, and a way to talk after a match. Existing options were either generic social feeds or nothing structured for campus housing.'
      }}
      metrics={[
        { value: 'Top', label: 'Luddy project award' },
        { value: '2', label: 'Semesters: plan, then build' },
        { value: 'PHP', label: 'No frameworks allowed' },
        { value: 'Lead', label: 'Product + implementation' }
      ]}
      outcome="Crimson Flags won the Luddy School Top Project Award, among other recognition from the school."
      solutionsLabel="What shipped"
      solutions={[
        {
          title: 'See it run',
          lead: 'Quiz, matches, chat, and verification in one walkthrough.',
          detail:
            'This is the product end to end: take the lifestyle quiz, see top matches, message people, and manage the account flows that keep the system honest.',
          video: 'https://www.youtube.com/embed/M3-SDItZ8XQ?si=bzAaffmu21dML6Cp',
          videoCaption: 'Capstone demo walkthrough of Crimson Flags.'
        },
        {
          title: 'Quiz & matching',
          lead: 'A personality and lifestyle quiz that surfaces “crimson flags,” then ranks the best fits.',
          detail:
            'Students answer questions about habits and preferences. Dealbreakers show up as crimson flags. A matching algorithm returns the top three candidates based on those answers, with an option to match someone directly by email or phone if they already know who they want.',
          images: [
            {
              src: CrimsonFlagsImage3,
              alt: 'Compatibility quiz',
              caption: 'Lifestyle quiz used for matching.'
            },
            {
              src: CrimsonFlagsImage6,
              alt: 'Top matches',
              caption: 'Top three matches from the algorithm.'
            },
            {
              src: CrimsonFlagsImage1,
              alt: 'How We Compare feature table',
              caption: 'How Crimson Flags stacked up against campus options.'
            },
            {
              src: CrimsonFlagsImage4,
              alt: 'Verified profile',
              caption: 'Verified profiles marked clearly.'
            }
          ]
        },
        {
          title: 'Chat, maps, and day-to-day use',
          lead: 'Once matched, students can talk and scout apartments without leaving the app.',
          detail:
            'Matches open into chat. Google Maps helps people look at apartments together. Notifications keep requests and messages from getting lost. The whole stack was plain PHP, MySQL, and JavaScript: the course barred frameworks like React or Bootstrap.',
          images: [
            {
              src: CrimsonFlagsImage5,
              alt: 'Desktop chat',
              caption: 'Chat with matches.'
            },
            {
              src: CrimsonFlagsImage7,
              alt: 'Mobile chat',
              caption: 'Same chat flow on mobile.'
            },
            {
              src: CrimsonFlagsImage2,
              alt: 'Google Maps apartment viewer',
              caption: 'Apartment viewing with Google Maps.'
            },
            {
              src: CrimsonFlagsImage8,
              alt: 'Notifications',
              caption: 'Notifications for match activity.'
            }
          ]
        },
        {
          title: 'Trust & admin tools',
          lead: 'Verification, reports, and quiz editing so the system could be run for real.',
          detail:
            'Users can request verification. Admins approve or deny those requests, edit quiz content, review reports, ban accounts, and promote other admins. That admin surface mattered as much as the student-facing match flow.',
          images: [
            {
              src: CrimsonFlagsImage9,
              alt: 'Admin quiz editor',
              caption: 'Admin quiz editor.'
            },
            {
              src: CrimsonFlagsImage4,
              alt: 'Verified badge detail',
              caption: 'Verification as a first-class trust signal.'
            }
          ]
        }
      ]}
      decisions={[
        {
          title: 'Lead the build, not just the slides',
          body: 'With the strongest web background on the team, I ran project lead duties and implemented most features so planning work actually shipped as a working product.'
        },
        {
          title: 'Make dealbreakers visible',
          body: 'The “crimson flag” idea turned lifestyle conflicts into something students could see before messaging, instead of discovering them after move-in.'
        },
        {
          title: 'Ship trust features early',
          body: 'Google login, verification, reporting, and admin controls were part of the core system, not polish left for later.'
        },
        {
          title: 'Respect the constraints',
          body: 'No frameworks meant every interaction was hand-built in PHP and JavaScript. That slowed some UI work and forced cleaner ownership of the full stack.'
        }
      ]}
      next={{ link: '/projects/qfwellness', label: 'QF Wellness' }}
    />
  );
};

export default transition(CrimsonFlags);
